import {
  type SalonStockerHttpTypes,
  resolveSpecialHeaders
} from "@repo/api-sdk";
import Typesense from "typesense";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import type { ConfigurationOptions } from "typesense/lib/Typesense/Configuration";
export type { Hit } from "instantsearch.js";

/**
 * Typesense configuration for the storefront
 *
 * This adapter connects React InstantSearch to your Typesense server.
 * It searches the 'products' collection that contains:
 * - Products with sellers, brands, categories
 * - Min/max pricing for each product
 * - Only products with published listings from active sellers
 */
const typesenseServerConfig: ConfigurationOptions = {
  nodes: [
    {
      url: import.meta.env.VITE_TYPESENSE_NODE_1_URL
    }
  ],
  additionalHeaders: resolveSpecialHeaders(),
  apiKey: import.meta.env.VITE_TYPESENSE_SEARCH_KEY
};

export const typesenseAdapter = new TypesenseInstantSearchAdapter({
  server: typesenseServerConfig,
  additionalSearchParameters: {
    // Fields to search in (these match your Typesense schema)
    query_by: "title,brand.name,categories.name,sellers.name,variants.ean",

    // NOTE: do NOT hardcode `facet_by` here. The adapter only derives facet_by
    // per-query from the active InstantSearch widgets when this is absent -
    // `facet_by: additionalSearchParameters.facet_by || _adaptFacetBy(params.facets)`
    // (typesense-instantsearch-adapter SearchRequestAdapter). A hardcoded list
    // forces EVERY request - including the disjunctive derived queries (e.g. the
    // category-removed query used to compute the Categories facet) - to also
    // return brand/supplier facet counts, which the helper then mis-sources. That
    // is why the Brands facet showed whole-catalog counts (L'Oreal 107) even with
    // a category refinement active (Hair = 73). Letting the adapter scope facet_by
    // per query keeps each facet count constrained by the other active
    // refinements. See salonstocker/storefront#82.

    // Enable fuzzy matching for typos
    num_typos: 2,

    // How many results per page
    per_page: 20,

    // Enable highlighting of matched terms
    highlight_full_fields: "title,brand.name",

    // Sort by relevance by default
    sort_by: "_text_match:desc"
  },
  // Configure collection-specific settings for sorting
  collectionSpecificSearchParameters: {
    products: {
      // Default sort for products index
    },
    "products/sort/meta.min_price:asc": {
      query_by: "title,brand.name,categories.name,sellers.name,variants.ean",
      sort_by: "meta.min_price:asc"
    },
    "products/sort/meta.min_price:desc": {
      query_by: "title,brand.name,categories.name,sellers.name,variants.ean",
      sort_by: "meta.min_price:desc"
    }
  }
});

// Get the InstantSearch instance for use in React components
export const searchClient = typesenseAdapter.searchClient;

/**
 * Index name - must match the collection name in Typesense
 */
export const PRODUCTS_INDEX = "products";

/**
 * Direct Typesense client for API access beyond InstantSearch
 * Use this for direct queries, filtering, and other operations
 */
export const typesenseClient = new Typesense.Client({
  ...typesenseServerConfig,
  connectionTimeoutSeconds: 2
});

export type TypesenseProduct = SalonStockerHttpTypes.StoreTypesenseProduct;
